import type { DataTableColumnDef } from 'components/data-table';
import type { PermitsResult } from 'types/api-responses';
import { parseLocalDateTime } from 'utils/dates';

export type PermitRow = PermitsResult & Record<string, unknown>;

export const permitsColumns: DataTableColumnDef<PermitRow>[] = [
  {
    id: 'uwi14',
    accessorKey: 'uwi14',
    header: 'UWI14',
    size: 160,
    meta: { type: 'text' },
  },
  {
    id: 'verifiedGeometry',
    accessorKey: 'verifiedGeometry',
    header: 'Verified Wellbore Geometry',
    size: 220,
    meta: { type: 'text' },
  },
  {
    id: 'permitHoleDirection',
    accessorKey: 'permitHoleDirection',
    header: 'Permit Hole Direction',
    size: 180,
    meta: { type: 'text' },
  },
  {
    id: 'activePermit',
    accessorKey: 'activePermit',
    header: 'Active Permit',
    size: 140,
    meta: { type: 'text' },
  },
  {
    id: 'permitPsa',
    accessorKey: 'permitPsa',
    header: 'Permit PSA',
    size: 130,
    meta: { type: 'text' },
  },
  {
    id: 'permitLease',
    accessorKey: 'permitLease',
    header: 'Permit Lease',
    size: 180,
    meta: { type: 'text' },
  },
  {
    id: 'permitNum',
    accessorKey: 'permitNum',
    header: 'Permit Num',
    size: 150,
    meta: { type: 'text' },
  },
  {
    id: 'permitLeaseType',
    accessorKey: 'permitLeaseType',
    header: 'Permit Lease Type',
    size: 220,
    meta: { type: 'text' },
  },
  {
    id: 'permitActivityStatus',
    accessorKey: 'permitActivityStatus',
    header: 'Permit Activity Status',
    size: 200,
    meta: { type: 'text' },
  },
  {
    id: 'permitLicensee',
    accessorKey: 'permitLicensee',
    header: 'Permit Licensee',
    size: 200,
    meta: { type: 'text' },
  },
  {
    id: 'originalPermitLicenseDate',
    accessorKey: 'originalPermitLicenseDate',
    header: 'Original Permit License Date',
    size: 230,
    meta: { type: 'date' },
  },
  {
    id: 'originalPermitReportDate',
    accessorKey: 'originalPermitReportDate',
    header: 'Original Permit Report Date',
    size: 230,
    meta: { type: 'date' },
  },
  {
    id: 'permitExpirationDate',
    accessorKey: 'permitExpirationDate',
    header: 'Permit Expiration Date',
    size: 190,
    meta: { type: 'date' },
  },
  {
    id: 'permitReissueDate',
    accessorKey: 'permitReissueDate',
    header: 'Permit Reissue Date',
    size: 180,
    meta: { type: 'date' },
  },
  {
    id: 'permitAbandonedDate',
    accessorKey: 'permitAbandonedDate',
    header: 'Permit Abandoned Date',
    size: 190,
    meta: { type: 'date' },
  },
  {
    id: 'permitBasin',
    accessorKey: 'permitBasin',
    header: 'Permit Basin',
    size: 200,
    meta: { type: 'text' },
  },
  {
    id: 'permitField',
    accessorKey: 'permitField',
    header: 'Permit Field',
    size: 160,
    meta: { type: 'text' },
  },
  {
    id: 'permitSubBasin',
    accessorKey: 'permitSubBasin',
    header: 'Permit Sub Basin',
    size: 220,
    meta: { type: 'text' },
  },
  {
    id: 'permitPlay',
    accessorKey: 'permitPlay',
    header: 'Permit Play',
    size: 220,
    meta: { type: 'text' },
  },
  {
    id: 'permitSubPlay',
    accessorKey: 'permitSubPlay',
    header: 'Permit Sub Play',
    size: 180,
    meta: { type: 'text' },
  },
  {
    id: 'permitPlayType',
    accessorKey: 'permitPlayType',
    header: 'Permit Play Type',
    size: 200,
    meta: { type: 'text' },
  },
  {
    id: 'permittedDepthFt',
    accessorKey: 'permittedDepthFt',
    header: 'Permitted Depth (ft)',
    size: 190,
    meta: { type: 'decimal', align: 'left' },
  },
  {
    id: 'permittedDepthTvdFt',
    accessorKey: 'permittedDepthTvdFt',
    header: 'Permitted Depth TVD (ft)',
    size: 220,
    meta: { type: 'decimal', align: 'left' },
  },
  {
    id: 'permitTargetFormation',
    accessorKey: 'permitTargetFormation',
    header: 'Permit Target Formation',
    size: 200,
    meta: { type: 'text' },
  },
  {
    id: 'permitTargetFormationCode',
    accessorKey: 'permitTargetFormationCode',
    header: 'Permit Target Formation Code',
    size: 230,
    meta: { type: 'text' },
  },
  {
    id: 'permitTargetFluid',
    accessorKey: 'permitTargetFluid',
    header: 'Permit Target Fluid',
    size: 180,
    meta: { type: 'text' },
  },
  {
    id: 'permitWaterDepthFt',
    accessorKey: 'permitWaterDepthFt',
    header: 'Permit Water Depth (ft)',
    size: 200,
    meta: { type: 'decimal', align: 'left' },
  },
  {
    id: 'anticipatedH2sPpm',
    accessorKey: 'anticipatedH2sPpm',
    header: 'Anticipated H2S (ppm)',
    size: 200,
    meta: { type: 'decimal', align: 'left' },
  },
  {
    id: 'anticipatedH2sInd',
    accessorKey: 'anticipatedH2sInd',
    header: 'Anticipated H2S Ind',
    size: 190,
    meta: { type: 'text' },
  },
  {
    id: 'permitClass',
    accessorKey: 'permitClass',
    header: 'Permit Class',
    size: 200,
    meta: { type: 'text' },
  },
  {
    id: 'permitInjClass',
    accessorKey: 'permitInjClass',
    header: 'Permit Inj Class',
    size: 160,
    meta: { type: 'text' },
  },
  {
    id: 'maxPermittedLiquidInjectionPressurePsi',
    accessorKey: 'maxPermittedLiquidInjectionPressurePsi',
    header: 'Max Permitted Liquid Injection Pressure (psi)',
    size: 320,
    meta: { type: 'decimal', align: 'left' },
  },
  {
    id: 'maxPermittedLiquidInjectionVol',
    accessorKey: 'maxPermittedLiquidInjectionVol',
    header: 'Max Permitted Liquid Injection Vol',
    size: 270,
    meta: { type: 'decimal', align: 'left' },
  },
  {
    id: 'maxPermittedLiquidInjectionVolUnit',
    accessorKey: 'maxPermittedLiquidInjectionVolUnit',
    header: 'Max Permitted Liquid Injection Vol Unit',
    size: 290,
    meta: { type: 'text' },
  },
  {
    id: 'permitLicenseDateQuality',
    accessorKey: 'permitLicenseDateQuality',
    header: 'Permit License Date Quality',
    size: 220,
    meta: { type: 'text' },
  },
  {
    id: 'permitLicenseDateRanking',
    accessorKey: 'permitLicenseDateRanking',
    header: 'Permit License Date Ranking',
    size: 220,
    meta: { type: 'text' },
  },
  {
    id: 'permitLicenseFiler',
    accessorKey: 'permitLicenseFiler',
    header: 'Permit License Filer',
    size: 200,
    meta: { type: 'text' },
  },
  {
    id: 'permitLicenseFilerPhone',
    accessorKey: 'permitLicenseFilerPhone',
    header: 'Permit License Filer Phone',
    size: 210,
    meta: { type: 'text' },
  },
  {
    id: 'permitLicenseNum',
    accessorKey: 'permitLicenseNum',
    header: 'Permit License Num',
    size: 180,
    meta: { type: 'text' },
  },
  {
    id: 'permitLicensePurpose',
    accessorKey: 'permitLicensePurpose',
    header: 'Permit License Purpose',
    size: 200,
    meta: { type: 'text' },
  },
  {
    id: 'permitLicenseStatus',
    accessorKey: 'permitLicenseStatus',
    header: 'Permit License Status',
    size: 190,
    meta: { type: 'text' },
  },
  {
    id: 'updatedDate',
    accessorKey: 'updatedDate',
    header: 'Updated Date',
    size: 200,
    meta: { type: 'datetime' },
    cell: (info) => {
      const value = info.getValue() as string | null;
      return value ? parseLocalDateTime(value, true) : '—';
    },
  },
];
